import { useCallback, useMemo } from 'react'
import type { CrudFilter } from '@refinedev/core'

import { useSyncTableWithUrl } from '../../shared/hooks/useSyncTableWithUrl'
import type { ProductStatus } from './productStatusConfig'

export function buildProductFilters(status: ProductStatus | '', name: string): CrudFilter[] {
  const filters: CrudFilter[] = []
  if (status) filters.push({ field: 'status', operator: 'eq', value: status })
  if (name) filters.push({ field: 'name', operator: 'eq', value: name })
  return filters
}

interface UseProductListFiltersParams {
  /** Filter values read from the URL (the single source of truth). */
  status: ProductStatus | ''
  name: string
  urlPage: number
  currentPage: number
  setFilters: (filters: CrudFilter[], behavior: 'replace') => void
  setCurrentPage: (page: number) => void
  refetch: () => void
}

/** Pushes the URL-derived filters into refine's table and exposes a working refresh. */
export function useProductListFilters({
  status,
  name,
  urlPage,
  currentPage,
  setFilters,
  setCurrentPage,
  refetch,
}: UseProductListFiltersParams) {
  const filters = useMemo(() => buildProductFilters(status, name), [status, name])
  useSyncTableWithUrl({ filters, urlPage, currentPage, setFilters, setCurrentPage })

  // Re-setting identical filters does not change refine's content-hashed query key,
  // so the old "refresh" never refetched anything.
  const handleRefresh = useCallback(() => refetch(), [refetch])

  return { handleRefresh } as const
}
